import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import React, { useContext, useEffect, useState } from "react";
import { HiCalendar, HiClock, HiExclamation, HiX } from "react-icons/hi";
import { AuthContext } from "../context/AuthContext";

const ExpiredContractsModal = ({ isOpen, onClose }) => {
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [reminderMessages, setReminderMessages] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);

  useEffect(() => {
    const fetchExpiringContracts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/bulk/expiring-contracts`,
          {
            headers: {
              "x-auth-token": authState.token,
            },
          }
        );

        if (response.data.reminderMessages && response.data.reminderMessages.length > 0) {
          setReminderMessages(response.data.reminderMessages);
        }
      } catch (error) {
        console.error("Error fetching expiring contracts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && authState.token) {
      fetchExpiringContracts();
    }
  }, [isOpen, authState.token]);

  const handleContractClick = (contract) => {
    setSelectedContract(contract);
  };

  const handleCloseContract = () => {
    setSelectedContract(null);
  };

  const getStatusColor = (daysRemaining) => {
    if (daysRemaining <= 0) return "bg-red-100 text-red-800";
    if (daysRemaining <= 7) return "bg-yellow-100 text-yellow-800";
    return "bg-blue-100 text-blue-800";
  };

  const modalVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", damping: 25, stiffness: 500 } },
    exit: { opacity: 0, y: 50, transition: { duration: 0.2 } }
  };

  const contractCardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { scale: 1.05, transition: { duration: 0.2 } }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-yellow-100 p-2 rounded-full">
                    <HiExclamation className="text-yellow-600 text-xl" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Expiring Contracts</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <HiX className="h-6 w-6" />
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-6">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  </div>
                ) : reminderMessages.length > 0 ? (
                  reminderMessages.map((message, index) => (
                    <div key={index}>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center space-x-2">
                        <HiCalendar className={message.daysRemaining <= 0 ? "text-red-500" : "text-blue-500"} />
                        <span>
                          {message.daysRemaining === 0
                            ? "Contracts Expired Today"
                            : message.daysRemaining === 1
                            ? "Contracts Expiring Today"
                            : `Contracts Expiring in ${message.daysRemaining} Days`}
                        </span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {message.contracts.map((contract, contractIndex) => (
                          <motion.div
                            key={contractIndex}
                            variants={contractCardVariants}
                            initial="hidden"
                            animate="visible"
                            whileHover="hover"
                            className="bg-white dark:bg-gray-700 rounded-lg shadow-md overflow-hidden cursor-pointer"
                            onClick={() => handleContractClick(contract)}
                          >
                            <div className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="text-lg font-semibold text-gray-800 dark:text-white truncate">
                                  {contract.contractNumber}
                                </h4>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.daysRemaining)}`}>
                                  {contract.daysRemaining <= 0 ? "Expired" : `${contract.daysRemaining} days left`}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 truncate">
                                {contract.contractDescription}
                              </p>
                              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                <HiClock className="mr-1" />
                                <span>{new Date(contract.endDate).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 dark:text-gray-400 text-center">No expiring contracts found.</p>
                )}
              </div>
              <div className="mt-6 text-xs font-bold text-gray-400 dark:text-gray-500 text-center">
                intelleX CLMS🤓
              </div>
            </div>
          </motion.div>

          <AnimatePresence>
            {selectedContract && (
              <motion.div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseContract}
              >
                <motion.div
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full m-4 overflow-hidden"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", damping: 25, stiffness: 500 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Contract Details</h3>
                      <button
                        onClick={handleCloseContract}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        <HiX className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Contract Number</label>
                        <p className="text-base text-gray-900 dark:text-white">{selectedContract.contractNumber}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                        <p className="text-base text-gray-900 dark:text-white">{selectedContract.contractDescription}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiration Date</label>
                        <p className="text-base text-gray-900 dark:text-white">
                          {new Date(selectedContract.endDate).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Days Remaining</label>
                        <p className={`text-base font-semibold ${getStatusColor(selectedContract.daysRemaining)}`}>
                          {selectedContract.daysRemaining <= 0 ? "Expired" : `${selectedContract.daysRemaining} days`}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExpiredContractsModal;